import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import PrivateRoute, { PublicRoute } from './components/common/PrivateRoute';
import { PageTransition } from './components/common/Animations';
import './index.css';

// 懒加载页面 - 使用预加载优化
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Profile = lazy(() => import('./pages/Profile'));
const Cart = lazy(() => import('./pages/Cart'));
const ConfidentialityAgreement = lazy(() => import('./pages/ConfidentialityAgreement'));
const AgreementAdmin = lazy(() => import('./pages/AgreementAdmin'));
const NotFound = lazy(() => import('./pages/NotFound'));

// 预加载函数
const preloadComponent = (component) => {
  const Comp = lazy(component);
  Comp.preload = component;
  return Comp;
};

// 为关键页面添加预加载
Home.preload = () => import('./pages/Home');
Login.preload = () => import('./pages/Login');
Dashboard.preload = () => import('./pages/Dashboard');

/**
 * 页面加载骨架屏
 */
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" role="status" aria-label="加载中">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-love-pink border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-600">加载中...</p>
    </div>
  </div>
);

/**
 * 带动画的路由组件
 */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* 公开页面 */}
        <Route 
          path="/" 
          element={
            <PageTransition>
              <Home />
            </PageTransition>
          } 
        />
        
        {/* 登录页 - 已登录用户重定向 */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <PageTransition>
                <Login />
              </PageTransition>
            </PublicRoute>
          }
        />
        
        {/* 需要登录的页面 */}
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <PageTransition>
                <Dashboard />
              </PageTransition>
            </PrivateRoute>
          } 
        />

        {/* 个人中心 - 仅普通用户可访问 */}
        <Route 
          path="/profile" 
          element={
            <PrivateRoute requireNormalUser>
              <PageTransition>
                <Profile />
              </PageTransition>
            </PrivateRoute>
          } 
        />

        {/* 购物车/订单页面 - 仅普通用户可访问 */}
        <Route 
          path="/cart" 
          element={
            <PrivateRoute requireNormalUser>
              <PageTransition>
                <Cart />
              </PageTransition>
            </PrivateRoute>
          } 
        />
        
        {/* 需要管理员权限 */}
        <Route 
          path="/admin" 
          element={
            <PrivateRoute requireAdmin>
              <PageTransition>
                <AdminPanel />
              </PageTransition>
            </PrivateRoute>
          } 
        />
        
        {/* 保密协议页面 - 需要登录 */}
        <Route 
          path="/agreement" 
          element={
            <PrivateRoute>
              <PageTransition>
                <ConfidentialityAgreement />
              </PageTransition>
            </PrivateRoute>
          } 
        />
        
        {/* 保密协议管理页面 - 仅管理员 */}
        <Route 
          path="/admin/agreements" 
          element={
            <PrivateRoute requireAdmin>
              <PageTransition>
                <AgreementAdmin />
              </PageTransition>
            </PrivateRoute>
          } 
        />
        
        {/* 404 页面 */}
        <Route 
          path="/404" 
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          } 
        />
        
        {/* 未知路由重定向到 404 */}
        <Route 
          path="*" 
          element={
            <PageTransition>
              <Navigate to="/404" replace />
            </PageTransition>
          } 
        />
      </Routes>
    </AnimatePresence>
  );
};

/**
 * 应用主组件
 */
function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <div className="min-h-screen bg-gradient-to-br from-love-cream via-pink-50 to-love-cream">
                <Suspense fallback={<PageLoader />}>
                  <AnimatedRoutes />
                </Suspense>
              </div>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
